/**
 * Pantalla 2, detalle (docs/FASE-1.md §6): cabecera de la organización (editar, suspender,
 * reactivar) y pestañas Miembros / Dominios / Suscripciones.
 */
import type { InviteMemberInput, Membership, Module, Organization, OrganizationDomain, UpdateMemberInput } from "@continnua/contracts";
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";
import { Link, useParams } from "react-router-dom";

import { domainsApi, membersApi, modulesApi, organizationsApi } from "../api/client";
import { Badge, toneForStatus } from "../components/Badge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { Field } from "../components/Field";
import { Modal } from "../components/Modal";
import { Tabs, useTabs } from "../components/Tabs";
import { toast } from "../components/Toast";
import { toDisplayMessage } from "../lib/errors";
import { formatDate } from "../lib/format";
import { OrganizationBilling } from "./Facturacion";
import { OrganizationSubscriptions } from "./Subscriptions";

const ORG_STATUS_LABEL: Record<string, string> = { active: "Activa", suspended: "Suspendida", closed: "Cerrada" };
const MEMBERSHIP_STATUS_LABEL: Record<string, string> = {
  active: "Activo",
  invited: "Invitado",
  suspended: "Suspendido",
  removed: "Eliminado",
};

// --- Cabecera: editar organización ---------------------------------------------------------------

function EditOrganizationModal({
  organization,
  onClose,
  onSaved,
}: {
  organization: Organization;
  onClose: () => void;
  onSaved: (org: Organization) => void;
}): ReactElement {
  const [legalName, setLegalName] = useState(organization.legalName);
  const [displayName, setDisplayName] = useState(organization.displayName);
  const [taxId, setTaxId] = useState(organization.taxId ?? "");
  const [country, setCountry] = useState(organization.country);
  const [timezone, setTimezone] = useState(organization.timezone);
  const [primaryColor, setPrimaryColor] = useState(organization.branding.primaryColor ?? "");
  const [logoUrl, setLogoUrl] = useState(organization.branding.logoUrl ?? "");
  const [initials, setInitials] = useState(organization.branding.initials ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const updated = await organizationsApi.update(organization.id, {
        legalName,
        displayName,
        country,
        timezone,
        taxId: taxId ? taxId : null,
        branding: {
          ...(primaryColor ? { primaryColor } : {}),
          ...(logoUrl ? { logoUrl } : {}),
          ...(initials ? { initials } : {}),
        },
      });
      onSaved(updated);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Editar organización" onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field label="Razón social">
          {(props) => <input {...props} required minLength={2} value={legalName} onChange={(e) => setLegalName(e.target.value)} />}
        </Field>
        <Field label="Nombre comercial">
          {(props) => <input {...props} required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />}
        </Field>
        <Field label="RUC">{(props) => <input {...props} value={taxId} onChange={(e) => setTaxId(e.target.value)} />}</Field>
        <Field label="País" hint="Código ISO-3166 de 2 letras.">
          {(props) => (
            <input {...props} required maxLength={2} minLength={2} value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} />
          )}
        </Field>
        <Field label="Zona horaria">
          {(props) => <input {...props} required value={timezone} onChange={(e) => setTimezone(e.target.value)} />}
        </Field>
        <Field label="Color primario" hint="Formato #RRGGBB, opcional.">
          {(props) => <input {...props} placeholder="#1f5f8b" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />}
        </Field>
        <Field label="URL del logo" hint="Opcional.">
          {(props) => <input {...props} type="url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} />}
        </Field>
        <Field label="Iniciales" hint="Hasta 3 caracteres, opcional.">
          {(props) => <input {...props} maxLength={3} value={initials} onChange={(e) => setInitials(e.target.value)} />}
        </Field>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SuspendOrganizationModal({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}): ReactElement {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason);
    } catch (err) {
      setError(toDisplayMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Suspender organización" onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field label="Motivo" hint="Opcional; queda registrado en la auditoría.">
          {(props) => <textarea {...props} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-danger" disabled={submitting}>
            {submitting ? "Suspendiendo…" : "Suspender"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function OrganizationHeader({ organization, onChanged }: { organization: Organization; onChanged: (org: Organization) => void }): ReactElement {
  const [editing, setEditing] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleReactivate(): Promise<void> {
    setBusy(true);
    try {
      const updated = await organizationsApi.reactivate(organization.id);
      onChanged(updated);
      toast.success("Organización reactivada.");
    } catch (err) {
      toast.error(toDisplayMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-header">
      <div>
        <h1>{organization.displayName}</h1>
        <p className="subtle">
          {organization.slug} · {organization.legalName} · <Badge tone={toneForStatus(organization.status)}>{ORG_STATUS_LABEL[organization.status] ?? organization.status}</Badge>
        </p>
      </div>
      <div className="button-row">
        <button type="button" className="button-secondary" onClick={() => setEditing(true)}>
          Editar
        </button>
        {organization.status === "suspended" ? (
          <button type="button" className="button-primary" disabled={busy} onClick={() => void handleReactivate()}>
            {busy ? "Reactivando…" : "Reactivar"}
          </button>
        ) : (
          <button type="button" className="button-danger" onClick={() => setSuspending(true)}>
            Suspender
          </button>
        )}
      </div>
      {editing && <EditOrganizationModal organization={organization} onClose={() => setEditing(false)} onSaved={(org) => { onChanged(org); setEditing(false); toast.success("Organización actualizada."); }} />}
      {suspending && (
        <SuspendOrganizationModal
          onClose={() => setSuspending(false)}
          onConfirm={async (reason) => {
            const updated = await organizationsApi.suspend(organization.id, reason || undefined);
            onChanged(updated);
            setSuspending(false);
            toast.success("Organización suspendida.");
          }}
        />
      )}
    </div>
  );
}

// --- Pestaña Miembros ------------------------------------------------------------------------

function ModuleRolesFieldset({
  modules,
  selected,
  onToggle,
}: {
  modules: readonly Module[];
  selected: Record<string, string[]>;
  onToggle: (moduleCode: string, roleCode: string, checked: boolean) => void;
}): ReactElement {
  return (
    <fieldset className="roles-fieldset">
      <legend>Roles por módulo</legend>
      {modules.length === 0 && <p className="subtle">No hay módulos registrados.</p>}
      {modules.map((module) => (
        <div key={module.code} className="roles-module">
          <p className="roles-module-name">{module.name}</p>
          {module.roles.map((role) => {
            const checked = selected[module.code]?.includes(role.code) ?? false;
            const id = `role-${module.code}-${role.code}`;
            return (
              <label key={role.code} className="checkbox-label" htmlFor={id}>
                <input
                  id={id}
                  type="checkbox"
                  checked={checked}
                  onChange={(event) => onToggle(module.code, role.code, event.target.checked)}
                />
                {role.name}
              </label>
            );
          })}
        </div>
      ))}
    </fieldset>
  );
}

function InviteMemberModal({
  orgId,
  modules,
  onClose,
  onInvited,
}: {
  orgId: string;
  modules: readonly Module[];
  onClose: () => void;
  onInvited: (membership: Membership) => void;
}): ReactElement {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [roles, setRoles] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function toggleRole(moduleCode: string, roleCode: string, checked: boolean): void {
    setRoles((prev) => {
      const current = prev[moduleCode] ?? [];
      const next = checked ? [...current, roleCode] : current.filter((code) => code !== roleCode);
      return { ...prev, [moduleCode]: next };
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const filteredRoles = Object.fromEntries(Object.entries(roles).filter(([, codes]) => codes.length > 0));
      const input: InviteMemberInput = { email, roles: filteredRoles, ...(fullName ? { fullName } : {}) };
      const membership = await membersApi.invite(orgId, input);
      onInvited(membership);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Invitar miembro" onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field label="Correo electrónico">
          {(props) => <input {...props} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label="Nombre completo" hint="Opcional; solo aplica si el usuario todavía no existe.">
          {(props) => <input {...props} value={fullName} onChange={(e) => setFullName(e.target.value)} />}
        </Field>
        <ModuleRolesFieldset modules={modules} selected={roles} onToggle={toggleRole} />
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting}>
            {submitting ? "Invitando…" : "Invitar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditMemberModal({
  orgId,
  membership,
  modules,
  onClose,
  onSaved,
}: {
  orgId: string;
  membership: Membership;
  modules: readonly Module[];
  onClose: () => void;
  onSaved: (membership: Membership) => void;
}): ReactElement {
  const [roles, setRoles] = useState<Record<string, string[]>>(membership.roles);
  const [status, setStatus] = useState<"active" | "suspended">(membership.status === "suspended" ? "suspended" : "active");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function toggleRole(moduleCode: string, roleCode: string, checked: boolean): void {
    setRoles((prev) => {
      const current = prev[moduleCode] ?? [];
      const next = checked ? [...current, roleCode] : current.filter((code) => code !== roleCode);
      return { ...prev, [moduleCode]: next };
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const input: UpdateMemberInput = { roles, status };
      const updated = await membersApi.update(orgId, membership.userId, input);
      onSaved(updated);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={`Editar a ${membership.user.fullName}`} onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field label="Estado de la membresía">
          {(props) => (
            <select {...props} value={status} onChange={(e) => setStatus(e.target.value as "active" | "suspended")}>
              <option value="active">Activo</option>
              <option value="suspended">Suspendido</option>
            </select>
          )}
        </Field>
        <ModuleRolesFieldset modules={modules} selected={roles} onToggle={toggleRole} />
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function MembersTab({ orgId, modules }: { orgId: string; modules: readonly Module[] }): ReactElement {
  const [items, setItems] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState<Membership | null>(null);
  const [removing, setRemoving] = useState<Membership | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await membersApi.list(orgId);
      setItems(page.items);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: DataTableColumn<Membership>[] = [
    { key: "user", header: "Usuario", render: (m) => `${m.user.fullName} (${m.user.email})` },
    {
      key: "roles",
      header: "Roles",
      render: (m) =>
        Object.entries(m.roles)
          .filter(([, codes]) => codes.length > 0)
          .map(([moduleCode, codes]) => `${moduleCode}: ${codes.join(", ")}`)
          .join(" · ") || "—",
    },
    { key: "status", header: "Estado", render: (m) => <Badge tone={toneForStatus(m.status)}>{MEMBERSHIP_STATUS_LABEL[m.status] ?? m.status}</Badge> },
    {
      key: "actions",
      header: "Acciones",
      render: (m) => (
        <div className="button-row">
          <button type="button" className="button-secondary button-small" onClick={() => setEditing(m)}>
            Editar
          </button>
          <button type="button" className="button-danger button-small" onClick={() => setRemoving(m)}>
            Quitar
          </button>
        </div>
      ),
    },
  ];

  return (
    <section>
      <div className="page-header">
        <h2>Miembros</h2>
        <button type="button" className="button-primary" onClick={() => setShowInvite(true)}>
          Invitar miembro
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <DataTable columns={columns} rows={items} rowKey={(m) => m.userId} emptyMessage={loading ? "Cargando…" : "No hay miembros."} />
      {showInvite && (
        <InviteMemberModal
          orgId={orgId}
          modules={modules}
          onClose={() => setShowInvite(false)}
          onInvited={(membership) => {
            setShowInvite(false);
            // El núcleo solo envía el correo de invitación a quien NO tiene cuenta todavía: a
            // alguien que ya la tiene se le añade a la organización y no se le manda nada. Sin
            // este aviso, el operador queda esperando un correo que nunca va a salir.
            if (membership.user.status === "active") {
              toast.success(
                "Se añadió a la organización. Ya tenía cuenta en Continnua, así que no se envió invitación: " +
                  "si olvidó su contraseña, puede recuperarla desde la pantalla de acceso.",
              );
            } else {
              toast.success("Invitación enviada.");
            }
            void load();
          }}
        />
      )}
      {editing && (
        <EditMemberModal
          orgId={orgId}
          membership={editing}
          modules={modules}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success("Miembro actualizado.");
            void load();
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Quitar miembro"
          message={`¿Quitar a ${removing.user.fullName} (${removing.user.email}) de la organización?`}
          confirmLabel="Quitar"
          danger
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await membersApi.remove(orgId, removing.userId);
              toast.success("Miembro eliminado.");
              setRemoving(null);
              void load();
            } catch (err) {
              toast.error(toDisplayMessage(err));
            }
          }}
        />
      )}
    </section>
  );
}

// --- Pestaña Dominios --------------------------------------------------------------------------

function CreateDomainModal({
  orgId,
  modules,
  onClose,
  onCreated,
}: {
  orgId: string;
  modules: readonly Module[];
  onClose: () => void;
  onCreated: (domain: OrganizationDomain) => void;
}): ReactElement {
  const [domain, setDomain] = useState("");
  const [moduleCode, setModuleCode] = useState(modules[0]?.code ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const created = await domainsApi.create(orgId, { domain, moduleCode });
      onCreated(created);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Nuevo dominio propio" onClose={onClose}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Field label="Dominio" hint="Ej.: acme.com">
          {(props) => <input {...props} required value={domain} onChange={(e) => setDomain(e.target.value.toLowerCase())} />}
        </Field>
        <Field label="Módulo">
          {(props) => (
            <select {...props} required value={moduleCode} onChange={(e) => setModuleCode(e.target.value)}>
              {modules.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="button-primary" disabled={submitting || modules.length === 0}>
            {submitting ? "Registrando…" : "Registrar dominio"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function DomainsTab({ orgId, modules }: { orgId: string; modules: readonly Module[] }): ReactElement {
  const [items, setItems] = useState<OrganizationDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await domainsApi.list(orgId);
      setItems(page.items);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleVerify(domainId: string): Promise<void> {
    setVerifying(domainId);
    try {
      const updated = await domainsApi.verify(orgId, domainId);
      setItems((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      toast.success("Dominio verificado.");
    } catch (err) {
      toast.error(toDisplayMessage(err));
    } finally {
      setVerifying(null);
    }
  }

  const columns: DataTableColumn<OrganizationDomain>[] = [
    { key: "domain", header: "Dominio", render: (d) => d.domain },
    { key: "module", header: "Módulo", render: (d) => d.moduleCode },
    {
      key: "verified",
      header: "Verificado",
      render: (d) =>
        d.verifiedAt ? (
          <Badge tone="success">{formatDate(d.verifiedAt)}</Badge>
        ) : (
          <>
            <Badge tone="warning">Pendiente</Badge>
            {/* Registro TXT que hay que publicar (docs/FASE-4.md §5.3): nombre y valor derivados
             * del propio dominio y token, así el operador no tiene que adivinarlos. */}
            <div className="field-hint">
              Publica un TXT en <code>{d.verificationRecordName}</code> con el valor <code>{d.verificationRecordValue}</code>
            </div>
          </>
        ),
    },
    {
      key: "actions",
      header: "Acciones",
      render: (d) =>
        d.verifiedAt ? (
          "—"
        ) : (
          <button type="button" className="button-secondary button-small" disabled={verifying === d.id} onClick={() => void handleVerify(d.id)}>
            {verifying === d.id ? "Verificando…" : "Verificar"}
          </button>
        ),
    },
  ];

  return (
    <section>
      <div className="page-header">
        <h2>Dominios propios</h2>
        <button type="button" className="button-primary" onClick={() => setShowCreate(true)} disabled={modules.length === 0}>
          Nuevo dominio
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <DataTable columns={columns} rows={items} rowKey={(d) => d.id} emptyMessage={loading ? "Cargando…" : "No hay dominios registrados."} />
      {showCreate && (
        <CreateDomainModal
          orgId={orgId}
          modules={modules}
          onClose={() => setShowCreate(false)}
          onCreated={(domain) => {
            setShowCreate(false);
            setItems((prev) => [domain, ...prev]);
            toast.success("Dominio registrado.");
          }}
        />
      )}
    </section>
  );
}

// --- Página ---------------------------------------------------------------------------------

export function OrganizationDetail(): ReactElement {
  const { orgId } = useParams<{ orgId: string }>();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useTabs("members");

  useEffect(() => {
    if (!orgId) return;
    const id = orgId;
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const [org, modulePage] = await Promise.all([organizationsApi.get(id), modulesApi.list()]);
        if (cancelled) return;
        setOrganization(org);
        setModules(modulePage.items);
      } catch (err) {
        if (!cancelled) setError(toDisplayMessage(err));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (!orgId) return <p className="field-error">Falta el identificador de la organización.</p>;

  if (error) {
    return (
      <section>
        <p className="field-error" role="alert">
          {error}
        </p>
        <Link to="/organizations">Volver a organizaciones</Link>
      </section>
    );
  }

  if (!organization) return <p>Cargando…</p>;

  return (
    <section>
      <p>
        <Link to="/organizations">← Organizaciones</Link>
      </p>
      <OrganizationHeader organization={organization} onChanged={setOrganization} />
      <Tabs
        items={[
          { key: "members", label: "Miembros" },
          { key: "domains", label: "Dominios" },
          { key: "subscriptions", label: "Suscripciones" },
          { key: "billing", label: "Facturación" },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === "members" && <MembersTab orgId={orgId} modules={modules} />}
      {tab === "domains" && <DomainsTab orgId={orgId} modules={modules} />}
      {tab === "subscriptions" && <OrganizationSubscriptions orgId={orgId} modules={modules} />}
      {tab === "billing" && <OrganizationBilling orgId={orgId} />}
    </section>
  );
}
